import { z } from 'zod'
import { StrapiID } from './common'
import { DiscountConfiguration } from './discounts'
import { RemoteImage } from './image'
import { PackageType } from './package'
import { PricesRegion } from './price'
import { Requirements } from './requirements'

export const DEFAULT_RESTRICT_TYPE = 'none'

export type DealExtraRestrictType = 'same' | 'none' | 'vf'

const restrictions: Record<
  DealExtraRestrictType,
  (source: PackageType, target: PackageType) => boolean
> = {
  same: (source, target) => source !== target,
  none: () => false,
  vf: (source, target) => {
    if (source === 'office' && target === 'sv') {
      return false
    }
    return true
  },
}

export const isPackageRestricted = (
  source?: PackageType,
  target?: PackageType,
  restrictType: DealExtraRestrictType = DEFAULT_RESTRICT_TYPE
) => {
  if (!source || !target || !restrictType) {
    return false
  }
  if (source === target) {
    return false
  }
  // Everything below is different types

  return restrictions[restrictType](source, target)
}

export const DealTypeArray = ['normal', 'tgbo'] as const
export const DealTypeSchema = z.enum(DealTypeArray)
export const DealType = DealTypeSchema.enum
export type DealType = z.infer<typeof DealTypeSchema>

export const DEFAULT_DEAL_TYPES: readonly DealType[] = ['normal'] as const

export type DealSettings = {
  modalUpsell?: boolean
  extra?: 'default' | 'select' | 'pending'
  upsellShowDiff?: boolean
  priceSuffix?: 'monthly'
  pricePrefix?: 'monthly'
  installments?: number
  upgradeTypes?: PackageType[]
  flow?: string
  /**
   * If this is true convert the full price to the instalment price
   */
  convertPrice?: boolean
  maxFramePrice?: PricesRegion
  minFramePrice?: PricesRegion
}

const DealSettings: Record<DealType, DealSettings> = {
  normal: {},
  tgbo: {
    upsellShowDiff: true,
    priceSuffix: 'monthly',
    convertPrice: false,
    installments: 24,
    modalUpsell: true,
    upgradeTypes: [],
  },
}

/**
 * Gets the deal settings, it merges the custom deal settings with the base settings
 */
export const getDealSettings = (deal?: Deal) => {
  const base = DealSettings[deal?.type || 'normal'] || DealSettings.normal
  return {
    ...base,
    ...deal?.settings,
  }
}

export const getConvertedPrice = (price: number, settings?: DealSettings) => {
  if (!settings) {
    return price
  }
  if (!settings.convertPrice || !settings.installments) {
    return price
  }
  const monthly = price / settings.installments
  return monthly
}

export const shouldShowMonthly = (settings?: DealSettings) => {
  if (!settings || !settings.installments) {
    return false
  }
  return !!settings.convertPrice && settings.installments > 1
}

export const getConvertedPriceRegion = (
  price: PricesRegion,
  settings: DealSettings
) => {
  const converted = Object.fromEntries(
    Object.entries(price).map(([key, value]) => [
      key,
      getConvertedPrice(value, settings),
    ])
  )
  return converted
}

export const CardFilterPriceSchema = z.enum(['lower_or_even', 'higher_or_even'])
export type CardFilterPrice = z.infer<typeof CardFilterPriceSchema>

export type CardFilter = {
  tag?: string
  type?: DealType
  tagsExcluded?: string[]
  price?: CardFilterPrice
}

export type OrderType = 'base' | 'extra'

// Limit this to flows we want to have different settings for
export const FlowScenarios = ['simplified'] as const

export type FlowScenario = (typeof FlowScenarios)[number]

/**
 * The order scenario is a combination of the order type and DealType or FlowScenario
 *
 * Technically we don't currently have normal.extra as the extra pair is then part of the base order
 */
export type OrderScenario =
  | `${OrderType}.${DealType}`
  | `${OrderType}.${FlowScenario}`

export type DealVariant = {
  id: StrapiID
  image?: RemoteImage
  title?: string | null
  desc?: string | null
  requires?: Requirements
  body?: string | null
}

export type Deal = {
  id: StrapiID
  baseDealId?: StrapiID
  packages: StrapiID[]
  title: string
  desc?: string | null
  body?: string | null
  code: string
  locale: string
  /**
   * Number of frames in the deal Only 1 or 2 are allowed for now
   */
  frames: number
  isActive: boolean

  minFramePrice?: PricesRegion
  image?: RemoteImage
  banner?: RemoteImage
  rank?: number
  discounts: DiscountConfiguration[]
  upgrades: StrapiID[]
  vfDeal?: StrapiID | null
  upgradeDeals?: StrapiID[]
  extra?: StrapiID | null
  tags?: string[]
  addFirstId?: boolean
  extraRestrictType?: DealExtraRestrictType
  localizations?: Record<string, StrapiID>
  type?: DealType | null
  frameRequirements?: Requirements
  settings?: Partial<DealSettings>
  filter?: CardFilter
  variants?: DealVariant[]
  translationPath?: string | null
}
